import type { OrganizationUser } from '@compass/client/types/organization_user';
import { Loader2 } from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';

import {
  AssignedRolesSection,
  type PendingChanges,
} from '@/components/settings/AssignedRolesSection';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useAddProgramUserRole,
  useChangeOrganizationUserRole,
  useChangeProgramUserRole,
  useOrganizationUserRoles,
  useRemoveProgramUserRole,
} from '@/hooks/useUserRoles';

interface ChangeRolesDialogProps {
  organizationId: string;
  user: OrganizationUser | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ChangeRolesDialog: React.FC<ChangeRolesDialogProps> = ({
  organizationId,
  user,
  open,
  onOpenChange,
}) => {
  // State for pending changes
  const [pendingChanges, setPendingChanges] = useState<PendingChanges>({
    addOrgRoles: new Set(),
    removeOrgRoles: new Set(),
    addProgramRoles: new Map(),
    removeProgramRoles: new Map(),
  });

  const [isSaving, setIsSaving] = useState(false);

  // Fetch user roles within this organization using OrganizationUserRoleService
  const { data: userRolesData, isLoading: isLoadingRoles } = useOrganizationUserRoles(
    organizationId,
    user?.user_id,
  );

  // Use fetched roles, falling back to roles from user object
  const userRoles = userRolesData?.user_roles || user?.roles || [];

  // Mutation hooks
  const changeOrgRoleMutation = useChangeOrganizationUserRole();
  const addProgramRoleMutation = useAddProgramUserRole();
  const removeProgramRoleMutation = useRemoveProgramUserRole();
  const changeProgramRoleMutation = useChangeProgramUserRole();

  // Check if there are any pending changes
  const hasPendingChanges =
    pendingChanges.addOrgRoles.size > 0 ||
    pendingChanges.removeOrgRoles.size > 0 ||
    pendingChanges.addProgramRoles.size > 0 ||
    pendingChanges.removeProgramRoles.size > 0;

  // Save all changes
  const handleSaveChanges = async () => {
    if (!user?.user_id) return;

    setIsSaving(true);

    try {
      // Handle organization role change (user always has an org role)
      // Use atomic change API - only need the new role ID
      if (pendingChanges.addOrgRoles.size > 0) {
        const newRoleId = [...pendingChanges.addOrgRoles][0];
        await changeOrgRoleMutation.mutateAsync({
          organization_id: organizationId,
          user_id: user.user_id,
          new_role_id: newRoleId,
        });
      }

      // Handle program roles (one role per org+program combination)
      const allProgramIds = new Set([
        ...pendingChanges.addProgramRoles.keys(),
        ...pendingChanges.removeProgramRoles.keys(),
      ]);

      for (const programId of allProgramIds) {
        const programAdds = pendingChanges.addProgramRoles.get(programId);
        const programRemoves = pendingChanges.removeProgramRoles.get(programId);
        const hasProgramAdd = programAdds && programAdds.size > 0;
        const hasProgramRemove = programRemoves && programRemoves.size > 0;

        if (hasProgramAdd && hasProgramRemove) {
          // Use atomic change API - replaces existing role with new one
          const newRoleId = [...programAdds][0];
          await changeProgramRoleMutation.mutateAsync({
            organization_id: organizationId,
            program_id: programId,
            user_id: user.user_id,
            new_role_id: newRoleId,
          });
        } else if (hasProgramRemove) {
          // Only remove (user leaving the program)
          const roleId = [...programRemoves][0];
          await removeProgramRoleMutation.mutateAsync({
            organization_id: organizationId,
            program_id: programId,
            user_id: user.user_id,
            role_id: roleId,
          });
        } else if (hasProgramAdd) {
          // Only add (user joining the program)
          const roleId = [...programAdds][0];
          await addProgramRoleMutation.mutateAsync({
            organization_id: organizationId,
            program_id: programId,
            user_id: user.user_id,
            role_id: roleId,
          });
        }
      }

      toast.success('User roles updated successfully');
      onOpenChange(false);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || error?.message || 'Failed to update user roles',
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-y-auto">
        <DialogHeader className="space-y-3 mb-2">
          <div className="flex items-center gap-3">
            <div>
              <DialogTitle className="text-xl">Change Role</DialogTitle>
              <DialogDescription>
                Change roles and permissions for {user.name || user.email}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          <AssignedRolesSection
            organizationId={organizationId}
            userRoles={userRoles}
            onChangeRoles={setPendingChanges}
            loading={isLoadingRoles}
          />
        </div>

        {hasPendingChanges && (
          <div className="text-sm text-amber-600">
            You have unsaved changes. Click &quot;Save Changes&quot; to apply them.
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSaveChanges} disabled={!hasPendingChanges || isSaving}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
